import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import MenuDrawer, { MENU_PROF, MENU_ALUNO } from "./components/MenuDrawer";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import { DrawerProvider } from "./context/DrawerContext";
import { ConfigProvider } from "./context/ConfigContext";
import { useConfig } from "./context/ConfigContext";
import { Icon, Avatar, Badge, MemaLogo } from "./components/ui";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import Cadastro from "./pages/Cadastro";
import Alunos from "./pages/Alunos";
import Aulas from "./pages/Aulas";
import Conteudos from "./pages/Conteudos";
import Pagamentos from "./pages/Pagamentos";
import Relatorio from "./pages/Relatorio";
import Financeiro from "./pages/Financeiro";
import Usuarios from "./pages/Usuarios";
import HorariosDisponiveis from "./pages/HorariosDisponiveis";
import AprovacaoAlunos from "./pages/AprovacaoAlunos";
import MeuPerfil from "./pages/MeuPerfil";
import Configuracoes from "./pages/Configuracoes";

function Sidebar() {
  const { tipo, nome, fotoUrl, logout } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();

  if (!tipo) return null;

  const isAluno = tipo === 'aluno';
  const groups = isAluno ? MENU_ALUNO : MENU_PROF;
  const displayName = nome || (isAluno ? 'Aluno' : 'Professora');
  const sub = isAluno ? 'Aluno' : 'Professora';

  return (
    <div className="mema-sidebar" style={{ display: 'none' }}>
      {/* Logo header */}
      <div style={{
        padding: 'var(--gap-3)',
        display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
        borderBottom: '1px solid var(--border-hairline)',
        flexShrink: 0,
      }}>
        {config.logo_url
          ? <img src={config.logo_url} alt="Logo" style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', objectFit: 'contain', flexShrink: 0 }}/>
          : <MemaLogo size={32}/>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: -0.2, color: 'var(--text-primary)' }}>{config.nome_sistema || 'Aulas da Mema'}</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>gestão de aulas</div>
        </div>
      </div>

      {/* Nav groups */}
      <div className="mema-scroll" style={{ flex: 1, overflow: 'auto', padding: '0 var(--gap-2) var(--gap-3)' }}>
        {groups.map((g, gi) => (
          <div key={gi} style={{ marginTop: 'var(--gap-2)' }}>
            <div style={{
              padding: '8px var(--gap-2) 4px',
              fontSize: 10, fontWeight: 600, letterSpacing: 0.6,
              color: 'var(--text-tertiary)', textTransform: 'uppercase',
            }}>{g.group}</div>
            {g.items.map((it) => {
              const active = location.pathname === it.path;
              return (
                <button key={it.id} onClick={() => navigate(it.path)} style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
                  width: '100%', height: 36, padding: '0 8px',
                  border: 'none',
                  background: active ? 'var(--bg-hover)' : 'transparent',
                  color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13.5, fontWeight: active ? 540 : 500,
                  letterSpacing: -0.1, transition: 'background .12s, color .12s',
                }}>
                  <Icon name={it.icon} size={16} style={{ color: active ? 'var(--accent)' : 'var(--text-tertiary)' }}/>
                  <span style={{ flex: 1, textAlign: 'left' }}>{it.label}</span>
                  {it.badge && <Badge tone="accent">{it.badge}</Badge>}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* User footer */}
      <div style={{
        padding: 'var(--gap-3)',
        borderTop: '1px solid var(--border-hairline)',
        display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
        flexShrink: 0,
      }}>
        {fotoUrl
          ? <img src={fotoUrl} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>
          : <Avatar name={displayName} size={36}/>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 540, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{displayName}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{sub}</div>
        </div>
        <button onClick={logout} style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: 'var(--radius-md)',
          cursor: 'pointer', border: 'none', background: 'transparent',
          color: 'var(--danger)',
        }}>
          <Icon name="logout" size={16}/>
        </button>
      </div>
    </div>
  );
}

function TabBar() {
  const { tipo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAluno = tipo === 'aluno';
  const tabs = isAluno
    ? [
        { id: 'home',      icon: 'home',     label: 'Início',    path: '/home' },
        { id: 'aulas',     icon: 'calendar', label: 'Aulas',     path: '/aulas' },
        { id: 'conteudos', icon: 'book',     label: 'Materiais', path: '/conteudos' },
        { id: 'perfil',    icon: 'user',     label: 'Perfil',    path: '/meu-perfil' },
      ]
    : [
        { id: 'home',   icon: 'home',     label: 'Início', path: '/home' },
        { id: 'aulas',  icon: 'calendar', label: 'Agenda', path: '/aulas' },
        { id: 'alunos', icon: 'users',    label: 'Alunos', path: '/alunos' },
        { id: 'perfil', icon: 'user',     label: 'Perfil', path: '/meu-perfil' },
      ];

  return (
    <div className="mema-tabbar" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50, display: 'flex', justifyContent: 'center' }}>
    <div style={{
      width: '100%', maxWidth: 480,
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      background: 'rgba(245,244,241,0.85)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderTop: '1px solid var(--border-hairline)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-around', padding: '6px 8px' }}>
        {tabs.map((t) => {
          const active = location.pathname === t.path;
          return (
            <button key={t.id} onClick={() => navigate(t.path)} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '6px 0', border: 'none', background: 'transparent', cursor: 'pointer',
              color: active ? 'var(--accent)' : 'var(--text-tertiary)',
              fontFamily: 'inherit', fontSize: 10, fontWeight: 540, letterSpacing: -0.1,
              transition: 'color .15s',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? 'var(--accent-soft)' : 'transparent',
                transition: 'background .15s',
              }}>
                <Icon name={t.icon} size={17} stroke={active ? 1.9 : 1.6}/>
              </div>
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
    </div>
  );
}

function AppShell() {
  const { tipo } = useAuth();
  const isLogged = !!tipo;

  return (
    <div className="mema-app-root" style={{ minHeight: '100vh', background: 'var(--bg-canvas)' }}>
      {isLogged && <Sidebar />}
      <div className={isLogged ? 'mema-main' : undefined}>
        <Routes>
          <Route path="/" element={isLogged ? <Navigate to="/home" /> : <Login />} />
          <Route path="/login" element={isLogged ? <Navigate to="/home" /> : <Login />} />
          <Route path="/cadastro" element={<Cadastro />} />
          <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/usuarios" element={<ProtectedRoute><Usuarios /></ProtectedRoute>} />
          <Route path="/aulas" element={<ProtectedRoute><Aulas /></ProtectedRoute>} />
          <Route path="/conteudos" element={<ProtectedRoute><Conteudos /></ProtectedRoute>} />
          <Route path="/pagamentos" element={<ProtectedRoute><Pagamentos /></ProtectedRoute>} />
          <Route path="/relatorio" element={<ProtectedRoute><Relatorio /></ProtectedRoute>} />
          <Route path="/financeiro" element={<ProtectedRoute><Financeiro /></ProtectedRoute>} />
          <Route path="/horarios-disponiveis" element={<ProtectedRoute><HorariosDisponiveis /></ProtectedRoute>} />
          <Route path="/alunos" element={<ProtectedRoute><Alunos /></ProtectedRoute>} />
          <Route path="/aprovar-alunos" element={<ProtectedRoute><AprovacaoAlunos /></ProtectedRoute>} />
          <Route path="/meu-perfil" element={<ProtectedRoute><MeuPerfil /></ProtectedRoute>} />
          <Route path="/configuracoes" element={<ProtectedRoute><Configuracoes /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to={isLogged ? "/home" : "/login"} />} />
        </Routes>
      </div>

      {isLogged && (
        <>
          <MenuDrawer />
          <TabBar />
        </>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <DrawerProvider>
        <ConfigProvider>
          <AppShell />
        </ConfigProvider>
      </DrawerProvider>
    </Router>
  );
}
